import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronRight, LoaderCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../../lib/utils";

const authButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-control font-medium whitespace-nowrap outline-none transition-[color,background-color,border-color,opacity] duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-progress [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        /** Near-black filled CTA; the only strong fill on the page. */
        primary:
          "h-11 bg-ink-soft px-5 text-md text-white hover:bg-ink disabled:opacity-70",
        /** Tinted alternative action ("Use SSO instead"). */
        secondary:
          "h-11 bg-tint px-5 text-md text-ink hover:bg-tint-deep disabled:opacity-70",
        /** Quiet inline action ("Go back", "Cancel", "Edit"). */
        link: "h-auto gap-1.5 text-base leading-5 text-ink-faint hover:text-ink focus-visible:outline-offset-4 disabled:opacity-70"
      },
      fullWidth: {
        true: "w-full",
        false: ""
      }
    },
    defaultVariants: {
      variant: "primary",
      fullWidth: false
    }
  }
);

type Props = React.ComponentProps<"button"> &
  VariantProps<typeof authButtonVariants> & {
    /** Disables the button, shows a spinner, and swaps the label for `loadingText`. */
    loading?: boolean;
    loadingText?: React.ReactNode;
    /** Moves the flow on: trailing chevron that nudges right on hover. */
    forward?: boolean;
  };

export function AuthButton({
  className,
  variant,
  fullWidth,
  loading = false,
  loadingText,
  forward = false,
  disabled,
  children,
  type = "button",
  ...props
}: Props) {
  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type === "submit" ? "submit" : "button"}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        authButtonVariants({ variant, fullWidth }),
        forward && "group",
        className
      )}
      {...props}
    >
      {loading && (
        <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
      )}
      {loading && loadingText !== undefined ? loadingText : children}
      {forward && !loading && (
        <ChevronRight
          className="size-3.5 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-0.5"
          strokeWidth={2.5}
          aria-hidden="true"
        />
      )}
    </button>
  );
}

type LinkProps = Omit<React.ComponentProps<typeof Link>, "className"> &
  VariantProps<typeof authButtonVariants> & { className?: string };

/** An in-app route styled as an `AuthButton` (e.g. "Sign In" on a notice card). */
export function AuthButtonLink({
  className,
  variant,
  fullWidth,
  ...props
}: LinkProps) {
  return (
    <Link
      className={cn(authButtonVariants({ variant, fullWidth }), className)}
      {...props}
    />
  );
}

/** Quiet text link styled like the `link` button, for anchors. */
export function AuthLink({
  className,
  children,
  ...props
}: React.ComponentProps<"a">) {
  return (
    <a
      className={cn(
        "rounded-control text-base leading-5 text-ink-faint transition-colors duration-150 hover:text-ink focus-visible:outline-offset-4",
        className
      )}
      {...props}
    >
      {children}
    </a>
  );
}
